// Runtime SEO helper for Decap CMS-driven metadata.
// Improves UX + social previews for client-side routing.
// (Crawlers often need prerendering/SSR for full SEO.)

function asString(v) {
  return v != null ? String(v).trim() : "";
}

function isHttpUrl(s) {
  return /^https?:\/\//i.test(String(s || ""));
}

function inferQueryParamFromExisting(selector, paramName) {
  if (typeof document === "undefined") return "";
  try {
    const el = document.head && document.head.querySelector(selector);
    if (!el) return "";
    const raw = el.getAttribute("content") || el.getAttribute("href") || "";
    if (!raw) return "";
    const u = new URL(raw, window.location.origin);
    return u.searchParams.get(paramName) || "";
  } catch {
    return "";
  }
}

function withVersion(href, version) {
  const v = asString(version);
  const h = asString(href);
  if (!h || !v) return h;
  if (h.includes("?v=") || h.includes("&v=")) return h;
  return h.includes("?") ? `${h}&v=${encodeURIComponent(v)}` : `${h}?v=${encodeURIComponent(v)}`;
}

function upsertMeta(attr, key, content) {
  if (typeof document === "undefined" || !document.head) return;
  const value = asString(content);
  if (!value) return;

  const head = document.head;
  let el = head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href, extraAttrs) {
  if (typeof document === "undefined" || !document.head) return;
  const value = asString(href);
  if (!value) return;

  const head = document.head;
  let el = head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    head.appendChild(el);
  }
  el.setAttribute("href", value);

  if (extraAttrs && typeof extraAttrs === "object") {
    Object.keys(extraAttrs).forEach((k) => {
      const v = extraAttrs[k];
      if (v != null && String(v) !== "") el.setAttribute(k, String(v));
    });
  }
}

function updateAllLinks(selector, href, extraAttrs) {
  if (typeof document === "undefined" || !document.head) return;
  const value = asString(href);
  if (!value) return;

  const head = document.head;
  const els = head.querySelectorAll(selector);

  if (els && els.length) {
    els.forEach((el) => {
      el.setAttribute("href", value);
      if (extraAttrs && typeof extraAttrs === "object") {
        Object.keys(extraAttrs).forEach((k) => {
          const v = extraAttrs[k];
          if (v != null && String(v) !== "") el.setAttribute(k, String(v));
        });
      }
    });
  } else {
    const rel = selector.includes("apple-touch-icon") ? "apple-touch-icon" : "icon";
    const el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("href", value);
    if (extraAttrs && typeof extraAttrs === "object") {
      Object.keys(extraAttrs).forEach((k) => {
        const v = extraAttrs[k];
        if (v != null && String(v) !== "") el.setAttribute(k, String(v));
      });
    }
    head.appendChild(el);
  }
}

function resolvePublicIconHref(rawFavicon) {
  const raw = asString(rawFavicon);
  if (!raw) return "";
  if (isHttpUrl(raw)) return raw;

  // CMS paths like /assets/images/foo.ico are NOT served by CRA.
  // We copy the chosen favicon into /public at build time.
  const lower = raw.toLowerCase();
  if (lower.endsWith(".ico")) return "/favicon.ico";

  return "/favicon-32x32.png";
}

function resolvePublicOgImageHref(rawOgImage) {
  const raw = asString(rawOgImage);
  if (!raw) return "";
  if (isHttpUrl(raw)) return raw;

  return "/og-image.png";
}

export function applySeo(seo) {
  if (typeof document === "undefined") return;
  if (!seo || typeof seo !== "object") return;

  const title = asString(seo.title);
  const description = asString(seo.description);
  const siteUrl = asString(seo.siteUrl || seo.url);
  const twitterHandle = asString(seo.twitterHandle);
  const themeColor = asString(seo.themeColor);

  const rawOgImage = asString(seo.ogImage || seo.image);
  const rawFavicon = asString(seo.favicon || seo.icon);

  if (title) document.title = title;

  upsertMeta("name", "title", title || document.title);
  upsertMeta("name", "description", description);
  if (themeColor) upsertMeta("name", "theme-color", themeColor);

  const origin = siteUrl ? siteUrl.replace(/\/+$/, "") : (typeof window !== "undefined" ? window.location.origin : "");
  if (siteUrl) upsertLink("canonical", siteUrl);

  upsertMeta("property", "og:title", title || document.title);
  upsertMeta("property", "og:description", description);
  if (siteUrl) upsertMeta("property", "og:url", siteUrl);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("property", "twitter:card", "summary_large_image");

  upsertMeta("name", "twitter:title", title || document.title);
  upsertMeta("property", "twitter:title", title || document.title);

  upsertMeta("name", "twitter:description", description);
  upsertMeta("property", "twitter:description", description);

  const ogV =
    inferQueryParamFromExisting('meta[property="og:image"]', "v") ||
    inferQueryParamFromExisting('meta[property="twitter:image"]', "v");

  const ogPath = resolvePublicOgImageHref(rawOgImage);
  if (ogPath) {
    const ogAbs = origin ? `${origin}${ogPath}` : ogPath;
    const ogFinal = withVersion(ogAbs, ogV);
    upsertMeta("property", "og:image", ogFinal);
    upsertMeta("name", "twitter:image", ogFinal);
    upsertMeta("property", "twitter:image", ogFinal);
  }

  if (twitterHandle) {
    const handle = twitterHandle.startsWith("@") ? twitterHandle : `@${twitterHandle}`;
    upsertMeta("name", "twitter:site", handle);
    upsertMeta("name", "twitter:creator", handle);
    upsertMeta("property", "twitter:site", handle);
    upsertMeta("property", "twitter:creator", handle);
  }

  const iconV =
    inferQueryParamFromExisting('link[rel="icon"][href*="?v="]', "v") ||
    inferQueryParamFromExisting('link[rel="shortcut icon"][href*="?v="]', "v");

  const iconPath = resolvePublicIconHref(rawFavicon);
  if (iconPath) {
    const iconFinal = withVersion(iconPath, iconV);
    const isIco = iconPath.toLowerCase().endsWith(".ico");

    updateAllLinks('link[rel="icon"]', iconFinal, isIco ? { type: "image/x-icon" } : null);
    updateAllLinks('link[rel="shortcut icon"]', iconFinal, isIco ? { type: "image/x-icon" } : null);

    // Only update apple-touch-icon if it's PNG (iOS expects PNG)
    if (!isIco) updateAllLinks('link[rel="apple-touch-icon"]', iconFinal);
  }
}
